import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
// @mui
import { Box, Card, CardHeader, styled } from '@mui/material';
// utils
import { fNumber } from 'src/utils/formatNumber';
// components
import { useChart } from 'src/components/chart';
import { t } from 'i18next';
import { glb_sv } from 'src/utils';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { size } from 'lodash';

const StyledTitle = styled('div')(({ theme }) => ({
    fontSize: '30px', // Default font size
    [theme.breakpoints.down('md')]: {
        fontSize: '27px', // Font size for medium screens
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '18px', // Font size for small screens
    },
    [theme.breakpoints.down('xs')]: {
        fontSize: '16px', // Font size for very small screens
    },
}));

const StyledSubheader = styled('div')(({ theme }) => ({
    fontSize: '23px', // Default font size
    [theme.breakpoints.down('md')]: {
        fontSize: '25px', // Font size for medium screens
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '16px', // Font size for small screens
    },
    [theme.breakpoints.down('xs')]: {
        fontSize: '14px', // Font size for very small screens
    },
}));

// ----------------------------------------------------------------------

LineChartCustom.propTypes = {
    title: PropTypes.string,
    subheader: PropTypes.string,
    chartData: PropTypes.array.isRequired,
    dateData: PropTypes.object.isRequired,
    label: PropTypes.string,
    type: PropTypes.string,
};

export default function LineChartCustom({
    title,
    subheader,
    chartColors,
    totalLabel = t('total'),
    chartData,
    dateData,
    label = 'label',
    type,
    ...other
}) {
    const { t } = useTranslation();

    const startDateCurrent = glb_sv.convertMultipleDate(dateData.start_date_current);
    const endDateCurrent = glb_sv.convertMultipleDate(dateData.end_date_current);

    const startDatePrevious = glb_sv.convertMultipleDate(dateData.start_date_previous);
    const endDatePrevious = glb_sv.convertMultipleDate(dateData.end_date_previous);

    const startDateOld = glb_sv.convertMultipleDate(dateData.start_date_old);
    const endDateOld = glb_sv.convertMultipleDate(dateData.end_date_old);

    const chartLabels = [
        `${t('from_date')} ${startDateOld} ${t('to_date').toLowerCase()} ${endDateOld}`,
        `${t('from_date')} ${startDatePrevious} ${t('to_date').toLowerCase()} ${endDatePrevious}`,
        `${t('from_date')} ${startDateCurrent} ${t('to_date').toLowerCase()} ${endDateCurrent}`
    ];

    const chartSeries = useRef([]);

    if (type === 'money' && chartData.length > 0) {
        chartSeries.current = chartData.map((i) => ({
            name: i[label],
            data: [i.total_result_price_old, i.total_result_price_previous, i.total_result_price_current]
        }));
    }

    if (type === 'qty' && chartData.length > 0) {
        chartSeries.current = chartData.map((i) => ({
            name: i[label],
            data: [i.total_qty_old, i.total_qty_previous, i.total_qty_current]
        }));
    }

    const chartOptions = useChart({
        xaxis: {
            categories: chartLabels,
        },
        stroke: {
            curve: 'straight',
        },
        markers: {
            size: 4,
            hover: {
                sizeOffset: 2
            }
        },
        dataLabels: {
            enabled: false,
            offsetX: -6,
            style: {
                fontSize: '12px',
                colors: ['#fff'],
            },
            formatter: function (val) {
                return fNumber(val);
            },
            offsetX: 0,
            dropShadow: {
                enabled: true,
            },
        },
        legend: {
            show: true,
            showForSingleSeries: true,
            fontSize: '14px',
            markers: {
                fillColors: ['#00C49F', '#FFBB28', '#0088FE'],
            },
            position: 'bottom',
            horizontalAlign: 'center',
            offsetY: 12,
            itemMargin: {
                vertical: 8,
            },
        },
        tooltip: {
            shared: true,
            intersect: false,    
            x: {
                show: true,
            },
            y: {
                formatter: (seriesName) => fNumber(seriesName),
                title: {
                    formatter: function () {
                        return '';
                    },
                },
            },
        },
    });

    return (
        <Card {...other}>
            <CardHeader title={<StyledTitle>{title}</StyledTitle>} subheader={<StyledSubheader>{subheader}</StyledSubheader>} />
            {chartData && (
                <Box sx={{ mx: 3 }} dir="ltr">
                    <ReactApexChart type="line" series={chartSeries.current} options={chartOptions} height={364} />
                </Box>
            )}
        </Card>
    );
}
